import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Cctv, Wifi, Code, Smartphone, Shield, Server,
  X, CheckCircle, ArrowRight, Sun, Home as HomeIcon, Cpu, Layers, Laptop, FileText
} from 'lucide-react';
import PageHeader from '../../components/PageHeader/PageHeader';
import './Services.css';

const RevealOnScroll = ({ children, className = '', delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (let i = 0; i < entries.length; i++) {
          setIsVisible(entries[i].isIntersecting);
        }
      },
      { threshold: 0.1 }
    );

    const current = domRef.current;
    if (current) observer.observe(current);

    return () => {
      if (current) observer.unobserve(current);
    };
  }, []);

  return (
    <div
      ref={domRef}
      className={`reveal-wrapper ${isVisible ? 'is-visible' : ''} ${className}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
};

export default function Services() {
  const [selectedService, setSelectedService] = useState(null);
  const [mailHref, setMailHref] = useState('');

  const brands = [
    { name: 'Dell', url: 'https://upload.wikimedia.org/wikipedia/commons/1/18/Dell_logo_2016.svg' },
    { name: 'HP', url: 'https://upload.wikimedia.org/wikipedia/commons/a/ad/HP_logo_2012.svg' },
    { name: 'Lenovo', url: 'https://upload.wikimedia.org/wikipedia/commons/b/b8/Lenovo_logo_2015.svg' },
    { name: 'Huawei', url: 'https://www.vectorlogo.zone/logos/huawei/huawei-ar21.svg' },
    { name: 'Asus', url: 'https://upload.wikimedia.org/wikipedia/commons/2/2e/ASUS_Logo.svg' },
    { name: 'Acer', url: 'https://upload.wikimedia.org/wikipedia/commons/0/00/Acer_2011.svg' },
    { name: 'Apple', url: 'https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg' }
  ];

  const servicesData = [
    {
      id: 1, title: 'IT Equipment Resale', icon: <Laptop size={32} />,
      shortDesc: 'Trusted reseller of laptops, desktops, servers, and networking gear.',
      fullDesc: 'As an authorized reseller, we supply and configure premium enterprise hardware tailored to your organizational needs. We source directly from top-tier manufacturers including Dell, HP, Lenovo, Huawei, Asus, and Apple. Whether you need a bulk order of office workstations or robust server racks, we handle the procurement and setup.',
      features: ['Laptops & Desktop Workstations', 'Servers & Networking Gear', 'Bulk Enterprise Procurement', 'Setup & Configuration']
    },
    {
      id: 2, title: 'Solar Installations', icon: <Sun size={32} />,
      shortDesc: 'Efficient solar energy and backup power solutions.',
      fullDesc: 'We install cutting-edge solar panels, inverters, and battery backup systems to ensure your home or enterprise remains powered seamlessly during outages.',
      features: ['Solar Panel Installation', 'Inverter Configurations', 'Battery Backup Systems', 'Energy Consumption Audits']
    },
    {
      id: 3, title: 'Smart Home Systems', icon: <HomeIcon size={32} />,
      shortDesc: 'Comprehensive automation for lighting, security, and climate.',
      fullDesc: 'Transform your living or workspace with our smart home integrations. Control lighting, temperature, access gates, and security cameras directly from your smartphone.',
      features: ['Automated Lighting', 'Climate Control Integration', 'Smart Locks & Access', 'Voice Command Setup']
    },
    {
      id: 4, title: 'Physical Security & CCTV', icon: <Cctv size={32} />,
      shortDesc: 'Professional installation of advanced CCTV and physical security systems.',
      fullDesc: 'We provide end-to-end physical security infrastructure setups, assessing blind spots and installing high-definition IP camera networks for continuous monitoring.',
      features: ['High-Definition IP Cameras', 'NVR & DVR Configurations', 'Access Control Systems', 'Remote Viewing Setup']
    },
    {
      id: 5, title: 'Fibre Infrastructure', icon: <Wifi size={32} />,
      shortDesc: 'High-speed, reliable fibre optic cabling and network setups.',
      fullDesc: 'Singularity Tech handles the meticulous installation of high-speed fibre optic networks including splicing, trenching, and indoor routing for zero packet loss.',
      features: ['Fibre Splicing & Testing', 'Commercial Network Routing', 'Hardware Configuration', 'Speed Optimization']
    },
    {
      id: 6, title: 'Web & Enterprise Systems', icon: <Code size={32} />,
      shortDesc: 'Modern, responsive web solutions for your digital presence.',
      fullDesc: 'Our development division builds powerful web applications using cutting-edge frameworks like React and Node.js for corporate portals and complex dashboards.',
      features: ['React.js Frontends', 'Custom Enterprise Dashboards', 'Database Architecture (SQL)', 'API Integrations']
    },
    {
      id: 7, title: 'Mobile App Development', icon: <Smartphone size={32} />,
      shortDesc: 'Native and cross-platform mobile applications for business.',
      fullDesc: 'We utilize Flutter and React Native to refactor web platforms into highly responsive mobile formats with seamless UI/UX design.',
      features: ['Flutter Development', 'React Native Frameworks', 'Cross-Platform Compatibility', 'UI/UX Design']
    },
    {
      id: 8, title: 'IT Helpdesk & Support', icon: <Shield size={32} />,
      shortDesc: 'Comprehensive technical troubleshooting and upkeep.',
      fullDesc: 'We offer robust IT Helpdesk management systems, hardware repairs, and software troubleshooting to keep your operations smooth and uncompromised.',
      features: ['Hardware Maintenance', 'Helpdesk Ticketing Systems', 'Software Troubleshooting', 'On-Site Repair']
    },
    {
      id: 9, title: 'Secure Cloud Hosting', icon: <Server size={32} />,
      shortDesc: 'Fast, reliable hosting for enterprise applications.',
      fullDesc: 'We handle the administration of cloud hosting for your enterprise applications, ensuring databases and scripts run optimally with robust security.',
      features: ['Cloud Server Administration', 'Database Management', 'Automated Backups', 'SSL & Security']
    }
  ];

  useEffect(() => {
    if (selectedService) {
      const subject = `Inquiry regarding: ${selectedService.title}`;
      const body = `I am interested in your ${selectedService.title} service.\n\nDescription:\n${selectedService.fullDesc}\n\n---------------------------\nADD ADDITIONAL INFORMATION BELOW:`;
      setMailHref(`mailto:info@singularitytech.co.za?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
    }
  }, [selectedService]);

  useEffect(() => {
    if (selectedService) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
    } else {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('modal-open');
    }
    return () => { 
      document.body.style.overflow = 'unset'; 
      document.body.classList.remove('modal-open');
    };
  }, [selectedService]);

  const serviceCardsElements = [];
  for (let i = 0; i < servicesData.length; i++) {
    const svc = servicesData[i];
    serviceCardsElements.push(
      <RevealOnScroll key={svc.id} delay={i * 0.1}>
        <div className="service-card-detailed">
          <div className="sc-icon-wrapper">{svc.icon}</div>
          <h3>{svc.title}</h3>
          <p>{svc.shortDesc}</p>
          <div style={{ display: 'flex', gap: '10px', marginTop: 'auto', flexWrap: 'wrap' }}>
            <button className="btn-learn-more" onClick={() => setSelectedService(svc)}>Learn More</button>
            {svc.id === 2 && (
              <Link to="/solar-assessment" className="btn-learn-more" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <FileText size={16} /> Form
              </Link>
            )}
          </div>
        </div>
      </RevealOnScroll>
    );
  }

  const marqueeBrandElements = [];
  const duplicatedBrands = [...brands, ...brands, ...brands]; 
  for (let j = 0; j < duplicatedBrands.length; j++) {
    marqueeBrandElements.push(
      <img key={j} src={duplicatedBrands[j].url} alt={`${duplicatedBrands[j].name} Logo`} className="brand-logo-img" />
    );
  }

  return (
    <div className="services-page">
      <PageHeader 
        title={<>End-to-End Solutions for <br /><span>Your Enterprise</span></>} 
        description="Bridging the gap between physical infrastructure, enterprise hardware supply, and digital innovation." 
      />

      <section className="services-main-content">
        <div className="floating-decoration deco-1"><Layers size={200} /></div>
        <div className="floating-decoration deco-2"><Cpu size={250} /></div>
        
        <div className="container">
          <div className="services-grid-layout">{serviceCardsElements}</div>
        </div>
      </section>

      <div className="brand-marquee-container">
        <div className="brand-marquee-track">
          {marqueeBrandElements}
        </div>
      </div>

      <div className={`modal-overlay ${selectedService ? 'active' : ''}`} onClick={() => setSelectedService(null)}>
        <div className="modal-content-box" onClick={(e) => e.stopPropagation()}>
          <button className="modal-close-btn" onClick={() => setSelectedService(null)}><X size={24} /></button>
          <div className="modal-header-visual"><div className="modal-icon-large">{selectedService?.icon}</div></div>
          <div className="modal-body">
            <h2>{selectedService?.title}</h2>
            <p className="modal-desc">{selectedService?.fullDesc}</p>
            <div className="modal-features-grid">
              {selectedService?.features.map((f, i) => (
                <div key={i} className="modal-feature-item"><CheckCircle size={20} color="var(--brand-green)" />{f}</div>
              ))}
            </div>
            <div className="modal-action-row">
              {selectedService && (
                <a
                  href={mailHref}
                  className="btn-primary"
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none', padding: '12px 24px' }}
                  onClick={() => setSelectedService(null)}
                >
                  Request this Service <ArrowRight size={18} />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}